import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import type {
  WorkflowDefinitionDocument,
  WorkflowStepChildLink,
  WorkflowTerminalOutcome,
} from './platform-workflow-definition-lifecycle.models';
import { PlatformWorkflowDefinitionLifecycleService } from './platform-workflow-definition-lifecycle.service';
import type { StepAuthoringChildDefinition } from './workflow-step-composition';
import { nextAutomationId } from './workflow-automation-authoring';
import {
  applyChildLinks,
  applyTerminalOutcome,
  childReferenceIdentity,
  childCode,
  childRuleFields,
  childWorkFingerprint,
  terminalOutcomeCode,
} from './workflow-step-child-work-authoring';
import type { WorkflowEditorPendingScope, WorkflowEditorBufferState } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';
import { WorkflowLibrarySelectorComponent } from './workflow-library-selector.component';
import type { WorkflowLibraryEntry } from './workflow-library-selector.types';
import { WorkflowDraftConfirmationService } from './workflow-draft-confirmation.service';
export type WorkflowChildLinkDraft = Omit<WorkflowStepChildLink, 'child' | 'maximumDepth'> & {
  child: WorkflowStepChildLink['child'] | null;
  maximumDepth: number | null;
};
type LinkDraft = WorkflowChildLinkDraft;
export interface WorkflowChildWorkDraftSnapshot {
  kind: 'related' | 'result' | null;
  rules: WorkflowStepChildLink[];
  rule: LinkDraft | null;
  outcome: WorkflowTerminalOutcome | null;
  originalId: string | null;
  originalOutcomeCode: string | null;
  baseline: string;
}
export interface WorkflowChildWorkOwnedDocumentChange {
  document: WorkflowDefinitionDocument; owner: string; source: string; view: string | null;
  nodeId: string; draft: WorkflowChildWorkDraftSnapshot;
}
export interface WorkflowChildWorkRuleSummary {
  rule: WorkflowStepChildLink;
  name: string;
  publication: string;
  results: string;
  mode: string;
  availability: string | null;
  fixedPolicySupported: boolean;
  returnPolicy: string;
  cancelPolicy: string;
  failurePolicy: string;
  depth: string;
}
interface ChildFormOrigin { generation: number; owner: string; source: string; view: string | null; rule: LinkDraft | null; outcome: WorkflowTerminalOutcome | null; }
@Component({
  selector: 'phms-workflow-step-child-work-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, UiButtonDirective, UiControlDirective, WorkflowLibrarySelectorComponent],
  templateUrl: './workflow-step-child-work-editor.component.html',
  styleUrl: './workflow-step-child-work-editor.component.scss',
})
export class WorkflowStepChildWorkEditorComponent
  implements OnChanges, OnDestroy
{
  @Input() document: WorkflowDefinitionDocument | null = null;
  @Input() nodeId: string | null = null;
  @Input() disabled = false;
  @Input() canAuthor = true;
  @Input() interactionBlocked = false;
  @Input() resetVersion = 0;
  @Input() workspaceMode = false;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Input() viewContext: (() => unknown) | null = null;
  @Input() active: (() => boolean) | null = null;
  @Input() activeView: (() => string | null) | null = null;
  @Input() activeViewState: string | null | undefined = undefined;
  @Output() pendingChangesChange = new EventEmitter<boolean>();
  @Output() documentChange = new EventEmitter<WorkflowDefinitionDocument>();
  @Output() ownedDocumentChange = new EventEmitter<WorkflowChildWorkOwnedDocumentChange>();
  private readonly api = inject(PlatformWorkflowDefinitionLifecycleService);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true }) ?? new WorkflowOwnerViewState();
  private readonly confirmation = inject(WorkflowDraftConfirmationService);
  private ownerSnapshot = '';
  private namespaceSnapshot = '';
  private renderedView: string | null = null;
  private formGeneration = 0;
  private focusRequest = 0;
  private destroyed = false;
  private restoring = false;
  private emitting: WorkflowChildWorkOwnedDocumentChange | null = null;
  private directMutation: { kind: 'related' | 'result'; id?: string } | null = null;
  pickerOpen = false;
  private pickerPurpose: 'add' | 'replace' = 'add';
  private pickerBaseline = '';
  private pickerSource = '';
  private pickerOwner = '';
  private pickerView: string | null = null;
  private pickerGeneration = 0;
  pickerSelected = '';
  private context = '';
  private catalogContext = '';
  private generation = 0;
  private fingerprint = '';
  private dirty = false;
  private originalId: string | null = null;
  private originalOutcomeCode: string | null = null;
  rules: WorkflowStepChildLink[] = [];
  draft: LinkDraft | null = null;
  outcomeDraft: WorkflowTerminalOutcome | null = null;
  choices: StepAuthoringChildDefinition[] = [];
  loading = false;
  catalogReady = false;
  childWorkUnavailableReason = '';
  loadError = '';
  error = '';
  get draftSnapshot(): WorkflowChildWorkDraftSnapshot {
    return structuredClone({ kind: this.directMutation?.kind ?? (this.draft ? 'related' : this.outcomeDraft ? 'result' : null), rules: this.rules,
      rule: this.draft, outcome: this.outcomeDraft, originalId: this.directMutation?.id ?? this.originalId, originalOutcomeCode: this.originalOutcomeCode, baseline: this.fingerprint });
  }
  get editorView() { return { origin: { generation: this.formGeneration, owner: this.ownerKey(), source: JSON.stringify(this.document),
    view: this.activeView?.() ?? null, rule: this.draft, outcome: this.outcomeDraft } satisfies ChildFormOrigin }; }
  private currentDocument() { return this.latestDocument ? this.latestDocument() : this.document; }
  private ownerKey() { return JSON.stringify(this.ownerContext?.() ?? null); }
  private documentContext(document: WorkflowDefinitionDocument | null) {
    return JSON.stringify([document?.workflowCode, this.workspaceMode ? document?.definitionVersionCode : null,
      this.workspaceMode ? document?.packReference : null, this.nodeId, this.resetVersion]);
  }
  private namespace(document = this.currentDocument()) { return JSON.stringify([this.documentContext(document), this.viewContext?.() ?? this.ownerContext?.() ?? null]); }
  private ownerCurrent() { return !this.destroyed && (!this.workspaceMode || this.ownerSnapshot === this.ownerKey() && this.namespaceSnapshot === this.namespace()); }
  private bufferCurrent(document: WorkflowDefinitionDocument | null): boolean {
    if (!document || !this.ownerCurrent() || this.documentContext(document) !== this.context || !Array.isArray(document.nodes)) return false;
    const nodes = document.nodes.filter(node => node?.id === this.nodeId);
    return nodes.length === 1 && (!this.fingerprint || childWorkFingerprint(document, this.nodeId) === this.fingerprint);
  }
  private logicalPresentationCurrent(): boolean {
    return this.ownerCurrent() && (!this.active || this.active()) && (!this.activeView || this.activeView() !== null
      && this.activeView() === this.renderedView && (this.activeViewState === undefined || this.activeViewState === this.renderedView));
  }
  private presentationCurrent(): boolean { return this.logicalPresentationCurrent() && !this.element.nativeElement.closest('[hidden],[inert],[aria-hidden="true"]'); }
  private nativeCurrent(origin?: ChildFormOrigin): boolean {
    return !this.workspaceMode || this.presentationCurrent() && !this.pickerOpen && !this.ownerViews.focusSuspended()
      && this.bufferCurrent(this.currentDocument()) && JSON.stringify(this.document) === JSON.stringify(this.currentDocument())
      && (!origin || origin.generation === this.formGeneration && origin.owner === this.ownerKey()
        && origin.source === JSON.stringify(this.currentDocument()) && origin.view === (this.activeView?.() ?? null)
        && origin.rule === this.draft && origin.outcome === this.outcomeDraft);
  }
  get ruleEditingDisabled() { return this.editingDisabled || this.workspaceMode && this.unavailableChild; }
  ruleValueChanged(key: 'label' | 'mode' | 'returnCode' | 'cancelCode' | 'failureCode' | 'maximumDepth', value: unknown, origin: ChildFormOrigin, model?: NgModel): void {
    if (this.restoring) return;
    if (!this.nativeCurrent(origin) || this.ruleEditingDisabled || !this.draft || (key === 'maximumDepth' ? value !== null && typeof value !== 'number' : typeof value !== 'string')) {
      this.restore(model, this.draft?.[key]); return;
    }
    Object.assign(this.draft, { [key]: value }); this.changed(origin);
  }
  private restore(model: NgModel | undefined, value: unknown): void {
    if (!model || this.restoring) return;
    this.restoring = true; try { model.control.setValue(value, { emitEvent: false }); } finally { this.restoring = false; }
  }
  private uniqueChoice(identity: string) {
    const matches = this.choices.filter(choice => choice && childReferenceIdentity(choice) === identity);
    return matches.length === 1 ? matches[0] : null;
  }
  private choiceReason(choice: StepAuthoringChildDefinition): string {
    if (!choice || this.choices.filter(item => item && childReferenceIdentity(item) === childReferenceIdentity(choice)).length !== 1)
      return 'This exact publication identity is ambiguous.';
    if (!Array.isArray(choice.terminalOutcomes) || choice.terminalOutcomes.some(outcome => !outcome || typeof outcome.code !== 'string' || typeof outcome.label !== 'string')
      || new Set(choice.terminalOutcomes.map(outcome => outcome.code)).size !== choice.terminalOutcomes.length) return 'Its published results are unavailable.';
    return choice.availableForNewUse ? '' : choice.unavailableReason || 'This publication is unavailable for new use.';
  }
  get pickerEntries(): readonly WorkflowLibraryEntry[] {
    return this.choices.filter(Boolean).map(choice => ({ id: childReferenceIdentity(choice), label: `${choice.displayName || choice.workflowCode} · ${choice.definitionVersionCode}`,
      description: `${choice.workflowCode} · ${Array.isArray(choice.terminalOutcomes) ? choice.terminalOutcomes.map(outcome => outcome?.label).join(', ') : 'Published results unavailable'}`,
      categoryId: 'publication', disabledReason: this.choiceReason(choice), actionLabel: 'Review publication' }));
  }
  get pickerSelection() { const choice = this.uniqueChoice(this.pickerSelected); return choice && !this.choiceReason(choice) ? choice : null; }
  acceptedResultsSummary(rule: WorkflowStepChildLink | null): string {
    const codes = rule?.acceptedOutcomeCodes;
    return !Array.isArray(codes) || codes.some(code => typeof code !== 'string') ? 'Saved accepted results are unavailable for inspection.'
      : codes.length ? `Accepted results: ${codes.join(', ')}` : 'No accepted results are configured.';
  }
  get editingExistingRule(): boolean { return this.draft !== null && this.originalId !== null; }
  get editingExistingOutcome(): boolean { return this.outcomeDraft !== null && this.originalOutcomeCode !== null; }
  get draftFixedPolicySupported(): boolean { return this.fixedPolicySupported(this.draft); }
  private fixedPolicySupported(rule: WorkflowStepChildLink | LinkDraft | null): boolean {
    return !!rule && rule.returnCode === 'retain_original_visit' && rule.cancelCode === 'stop_pending_leave_running' && rule.failureCode === 'blocked';
  }
  ruleSummary(rule: WorkflowStepChildLink): WorkflowChildWorkRuleSummary {
    const value = (raw: unknown) => typeof raw === 'string' ? raw : JSON.stringify(raw) ?? 'Unavailable';
    const reference = rule?.child;
    const referenceSupported = !!reference && typeof reference === 'object' && !Array.isArray(reference)
      && Object.keys(reference).every(key => ['workflowCode', 'definitionVersionCode', 'contentHash'].includes(key))
      && childCode(reference.workflowCode) && typeof reference.definitionVersionCode === 'string' && !!reference.definitionVersionCode.trim()
      && typeof reference.contentHash === 'string' && /^[a-f0-9]{64}$/.test(reference.contentHash);
    const matches = referenceSupported ? this.choices.filter(choice => choice && childReferenceIdentity(choice) === childReferenceIdentity(reference)) : [];
    const choice = matches.length === 1 ? matches[0] : null;
    const vocabulary = choice && Array.isArray(choice.terminalOutcomes) && choice.terminalOutcomes.every(outcome => outcome
      && childCode(outcome.code) && typeof outcome.label === 'string' && !!outcome.label.trim())
      && new Set(choice.terminalOutcomes.map(outcome => outcome.code)).size === choice.terminalOutcomes.length ? choice.terminalOutcomes : null;
    const codes = rule?.acceptedOutcomeCodes;
    const codesSupported = Array.isArray(codes) && codes.every(code => typeof code === 'string') && new Set(codes).size === codes.length;
    const labels = codesSupported && vocabulary ? codes.map(code => vocabulary.find(outcome => outcome.code === code)?.label ?? `Unknown result (${code})`) : null;
    const reasons: string[] = [];
    if (!this.canInspectEdit(rule)) reasons.push('Saved settings use an unsupported contract; inspect the retained values.');
    if (!childCode(rule?.id) || typeof rule?.label !== 'string' || !rule.label.trim() || rule.label.length > 160
      || !Number.isInteger(rule?.maximumDepth) || rule.maximumDepth < 1 || rule.maximumDepth > 16)
      reasons.push('The saved name, identity or depth limit needs review.');
    if (this.ambiguous) reasons.push('Saved related-work identities are ambiguous.');
    if (this.loading || !this.catalogReady) reasons.push('Exact publication availability has not been established.');
    else if (this.childWorkUnavailableReason) reasons.push(this.childWorkUnavailableReason);
    else if (!referenceSupported) reasons.push('The saved publication reference is unavailable.');
    else if (matches.length !== 1) reasons.push(matches.length ? 'This exact publication identity is ambiguous.' : 'The exact saved child publication is unavailable.');
    else if (!vocabulary) reasons.push('Its published results are unavailable.');
    else if (!choice!.availableForNewUse) reasons.push(choice!.unavailableReason || 'This publication is unavailable for new use.');
    if (!codesSupported) reasons.push('Saved accepted results are unavailable for inspection.');
    else if (!codes.length) reasons.push('No accepted results are configured.');
    else if (vocabulary && codes.some(code => !vocabulary.some(outcome => outcome.code === code))) reasons.push('Some saved results are not declared by this exact publication.');
    const fixedPolicySupported = this.fixedPolicySupported(rule);
    return { rule, name: typeof rule?.label === 'string' && rule.label ? rule.label : 'Saved related work',
      publication: choice ? `${choice.displayName || choice.workflowCode} · ${choice.definitionVersionCode} (${choice.workflowCode})`
        : reference ? `${value(reference.workflowCode)} · ${value(reference.definitionVersionCode)}` : 'Saved publication unavailable',
      results: labels ? labels.length ? `Accepted results: ${labels.join(', ')}` : 'No accepted results are configured.'
        : codesSupported ? `Saved result codes: ${codes.join(', ') || 'None'}` : 'Saved accepted results unavailable',
      mode: rule?.mode === 'blocking' ? 'Required result for this visit' : rule?.mode === 'observational' ? 'Tracked related work' : `Unavailable saved mode: ${value(rule?.mode)}`,
      availability: reasons.length ? reasons.join(' ') : null, fixedPolicySupported,
      returnPolicy: rule?.returnCode === 'retain_original_visit' ? 'Keep the relationship with the original visit.' : `Unavailable saved return rule: ${value(rule?.returnCode)}`,
      cancelPolicy: rule?.cancelCode === 'stop_pending_leave_running' ? 'Stop pending creation; leave a running child independent.' : `Unavailable saved cancellation rule: ${value(rule?.cancelCode)}`,
      failurePolicy: rule?.failureCode === 'blocked' ? 'Leave the result unaccepted.' : `Unavailable saved failure rule: ${value(rule?.failureCode)}`,
      depth: Number.isInteger(rule?.maximumDepth) && rule.maximumDepth >= 1 && rule.maximumDepth <= 16 ? String(rule.maximumDepth) : `Unavailable saved depth: ${value(rule?.maximumDepth)}` };
  }
  get pickerContext(): string { return JSON.stringify([this.pickerGeneration, this.ownerKey(), this.currentDocument(), this.activeView?.() ?? null, this.choices]); }
  get pickerActive(): boolean { return this.logicalPresentationCurrent() && !this.element.nativeElement.closest('[hidden],[inert]'); }
  private pickerCurrent(): boolean {
    return this.pickerOpen && this.pickerOwner === this.ownerKey() && this.pickerSource === JSON.stringify(this.currentDocument())
      && this.pickerView === (this.activeView?.() ?? null) && this.pickerBaseline === JSON.stringify(this.draftSnapshot)
      && this.pickerActive && !this.editingDisabled;
  }
  openPublicationPicker(origin?: ChildFormOrigin): void {
    if (!this.workspaceMode || !this.nativeCurrent(origin) || this.editingDisabled || this.terminal) return;
    if (!this.draft && this.dirty) return;
    this.pickerPurpose = this.draft ? 'replace' : 'add'; this.pickerSelected = '';
    this.pickerBaseline = JSON.stringify(this.draftSnapshot); this.pickerSource = JSON.stringify(this.currentDocument());
    this.pickerOwner = this.ownerKey(); this.pickerView = this.activeView?.() ?? null; this.pickerGeneration++; this.pickerOpen = true;
  }
  reviewPublication(identity: string): void {
    const choice = this.uniqueChoice(identity);
    if (this.pickerCurrent() && choice && !this.choiceReason(choice)) this.pickerSelected = identity;
  }
  async configurePublication(context: string): Promise<void> {
    const choice = this.pickerSelection;
    if (!this.pickerCurrent() || context !== this.pickerContext || !choice || this.choiceReason(choice)) return;
    const identity = childReferenceIdentity(choice), baseline = this.pickerBaseline;
    if (this.pickerPurpose === 'replace' && this.draft?.child && childReferenceIdentity(this.draft.child) !== identity) {
      const confirmed = await this.confirmation.confirm({ heading: 'Replace the child publication?',
        message: 'The exact publication will change. All accepted results will be cleared so you can select them deliberately for this publication.',
        confirmText: 'Replace publication', cancelText: 'Keep current publication' });
      if (!confirmed || !this.pickerCurrent() || context !== this.pickerContext || baseline !== this.pickerBaseline) return;
    }
    const current = this.uniqueChoice(identity);
    if (!current || this.choiceReason(current)) return;
    const child = { workflowCode: current.workflowCode, definitionVersionCode: current.definitionVersionCode, contentHash: current.contentHash };
    if (this.pickerPurpose === 'add') this.startNewDraft(child);
    else if (this.draft && (!this.draft.child || childReferenceIdentity(this.draft.child) !== identity)) {
      this.draft = { ...this.draft, child, acceptedOutcomeCodes: [] }; this.dirty = true; this.error = ''; this.pendingChangesChange.emit(true);
    }
    this.pickerOpen = false; this.formGeneration++; this.focusPendingChanges();
  }
  publicationPickerClosed(): void { this.pickerOpen = false; }
  get node() {
    const matches =
      (Array.isArray(this.document?.nodes) ? this.document.nodes : []).filter((node) => node?.id === this.nodeId);
    return matches.length === 1 ? matches[0] : null;
  }
  get terminal() {
    return this.node?.typeCode === 'terminal';
  }
  get editingDisabled() {
    return (
      this.disabled ||
      !this.canAuthor ||
      this.interactionBlocked ||
      !this.node ||
      this.loading ||
      !this.catalogReady ||
      !!this.childWorkUnavailableReason || this.opaqueRules || !this.ownerCurrent()
    );
  }
  get ambiguous() {
    return (
      new Set(this.rules.map((rule) => rule?.id)).size !== this.rules.length
    );
  }
  get availableChoices() {
    return this.choices.filter((choice) => choice && !this.choiceReason(choice));
  }
  get childSelection() {
    return this.draft?.child ? childReferenceIdentity(this.draft.child) : '';
  }
  get selectedChild() {
    return this.draft?.child
      ? this.uniqueChoice(this.childSelection)
      : null;
  }
  get unavailableChild() {
    return !!this.draft?.child && (!this.selectedChild || !!this.choiceReason(this.selectedChild));
  }
  get opaqueRules() {
    const declaration = this.node?.declaration;
    return declaration != null && (typeof declaration !== 'object' || Array.isArray(declaration) || !declaration.behavior
      || typeof declaration.behavior !== 'object' || Array.isArray(declaration.behavior)
      || declaration.behavior.childLinks != null && !Array.isArray(declaration.behavior.childLinks));
  }
  private savedRules() { const raw = this.node?.declaration?.behavior?.childLinks; return Array.isArray(raw) ? structuredClone(raw) : []; }
  get selectedOutcomes() { return this.selectedChild && !this.choiceReason(this.selectedChild) ? this.selectedChild.terminalOutcomes : []; }
  get outcomeEditable() {
    const outcome = this.node?.terminalOutcome;
    return (
      outcome == null ||
      (typeof outcome === 'object' && !Array.isArray(outcome) && outcome.version === 1 && childCode(outcome.code) && typeof outcome.label === 'string' &&
        Object.keys(outcome).every((key) =>
          ['version', 'code', 'label'].includes(key),
        ))
    );
  }
  ngOnChanges(changes: SimpleChanges = {}) {
    const view = this.activeView?.() ?? null;
    if ((this.activeViewState === undefined || this.activeViewState === view) && view !== this.renderedView) {
      this.renderedView = view; this.formGeneration++; this.focusRequest++;
    }
    const context = this.documentContext(this.document);
    if (this.workspaceMode && this.dirty && changes['document'] && this.namespaceSnapshot === this.namespace(this.document)
      && context === this.context && this.fingerprint === childWorkFingerprint(this.document!, this.nodeId)) this.ownerSnapshot = this.ownerKey();
    if (context !== this.context) {
      if (!this.workspaceMode || !this.dirty || changes['resetVersion']) { this.context = context; this.resetPendingChanges(); }
    } else if (!this.dirty && !this.draft && !this.outcomeDraft)
      this.rules = this.savedRules();
    if (!this.dirty && (changes['document'] || changes['ownerContext'] || changes['activeViewState'] && this.activeViewState === view)) {
      this.ownerSnapshot = this.ownerKey(); this.namespaceSnapshot = this.namespace(this.document);
    }
    const catalogContext = JSON.stringify([
      this.document?.workflowCode,
      this.document?.packReference,
      this.document?.scopeTypeCode,
      this.document?.scopeKey,
      this.node?.typeCode,
      this.workspaceMode ? this.ownerKey() : null,
    ]);
    if (catalogContext !== this.catalogContext) {
      this.generation++;
      this.loading = false;
      this.catalogContext = catalogContext;
      this.choices = [];
      this.catalogReady = false;
      this.childWorkUnavailableReason = '';
      if (this.document) void this.loadCatalog();
    }
  }

  ngOnDestroy() {
    this.generation++; this.destroyed = true; this.focusRequest++;
  }
  async loadCatalog() {
    if (!this.document) return;
    const generation = ++this.generation, owner = this.ownerKey();
    this.loading = true;
    this.loadError = '';
    try {
      const catalog = await firstValueFrom(
        this.api.getStepAuthoringCatalog(this.document),
      );
      if (generation !== this.generation || this.workspaceMode && owner !== this.ownerKey()) return;
      if (catalog.version !== 1 || !Array.isArray(catalog.childDefinitions))
        throw new Error('unavailable');
      if (
        catalog.childWorkUnavailableReason != null &&
        typeof catalog.childWorkUnavailableReason !== 'string'
      )
        throw new Error('unavailable');
      this.childWorkUnavailableReason =
        catalog.childWorkUnavailableReason ?? '';
      this.choices = catalog.childDefinitions;
      this.catalogReady = true;
    } catch {
      if (generation !== this.generation) return;
      this.choices = [];
      this.catalogReady = false;
      this.loadError = this.terminal
        ? 'Workflow result authoring availability could not be checked. Saved settings and entered input are kept.'
        : 'Published child workflow versions could not be checked. Saved settings and entered input are kept.';
    } finally {
      if (generation === this.generation) this.loading = false;
    }
  }
  canInspectEdit(rule: WorkflowStepChildLink) {
    return (
      rule?.version === 1 &&
      Object.keys(rule).every((key) => childRuleFields.includes(key)) &&
      !!rule.child &&
      Object.keys(rule.child).every((key) =>
        ['workflowCode', 'definitionVersionCode', 'contentHash'].includes(key),
      ) &&
      Array.isArray(rule.acceptedOutcomeCodes) &&
      ['blocking', 'observational'].includes(rule.mode) &&
      rule.returnCode === 'retain_original_visit' &&
      rule.cancelCode === 'stop_pending_leave_running' &&
      rule.failureCode === 'blocked'
    );
  }
  beginAdd(origin?: ChildFormOrigin) {
    if (this.workspaceMode) {
      if (!this.nativeCurrent(origin) || this.editingDisabled || this.terminal || this.ambiguous || this.rules.length >= 16) return;
      if (this.dirty) { this.error = 'Apply or cancel the current related-work input before adding another relationship.'; this.focusPendingChanges(); return; }
      this.openPublicationPicker(origin); return;
    }
    if (
      this.editingDisabled ||
      this.terminal ||
      this.ambiguous ||
      this.rules.length >= 16 ||
      !this.applyPendingChanges()
    )
      return;
    this.startNewDraft(null);
    this.focusPendingChanges();
  }
  private startNewDraft(child: WorkflowStepChildLink['child'] | null): void {
    this.fingerprint = childWorkFingerprint(this.currentDocument()!, this.nodeId);
    this.originalId = null;
    const used = this.document!.nodes.flatMap(node => {
      const rules = node.declaration?.behavior?.childLinks;
      return Array.isArray(rules) ? rules.filter(rule => rule && typeof rule.id === 'string').map(rule => rule.id) : [];
    });
    this.draft = {
      version: 1,
      id: nextAutomationId('related-work', used),
      label: '',
      child,
      mode: '',
      acceptedOutcomeCodes: [],
      returnCode: '',
      cancelCode: '',
      failureCode: '',
      maximumDepth: null,
    };
    this.formGeneration++; this.dirty = true; this.error = ''; this.pendingChangesChange.emit(true);
  }
  select(rule: WorkflowStepChildLink, origin?: ChildFormOrigin) {
    if (this.workspaceMode && this.dirty) { this.error = 'Apply or cancel the current related-work input before inspecting another rule.'; this.focusPendingChanges(); return; }
    if (
      !this.nativeCurrent(origin) || !this.rules.includes(rule) ||
      this.disabled ||
      this.interactionBlocked ||
      this.loading ||
      !this.node ||
      this.ambiguous ||
      !this.canInspectEdit(rule) ||
      !this.applyPendingChanges()
    )
      return;
    this.fingerprint = childWorkFingerprint(this.document!, this.nodeId);
    this.originalId = rule.id;
    this.draft = structuredClone(rule);
    this.formGeneration++;
    this.error = '';
    this.focusPendingChanges();
  }
  chooseChild(identity: string) {
    if (this.workspaceMode) return;
    if (this.editingDisabled || !this.draft) return;
    const child = this.uniqueChoice(identity);
    if (identity && (!child || this.choiceReason(child))) return;
    this.draft.child = child
      ? {
          workflowCode: child.workflowCode,
          definitionVersionCode: child.definitionVersionCode,
          contentHash: child.contentHash,
        }
      : null;
    this.draft.acceptedOutcomeCodes = [];
    this.changed();
  }
  toggleOutcome(code: string, checked: boolean, origin?: ChildFormOrigin, model?: NgModel) {
    if (
      !this.nativeCurrent(origin) || this.ruleEditingDisabled ||
      !this.draft ||
      !this.selectedChild?.availableForNewUse ||
      !this.selectedChild.terminalOutcomes.some(
        (outcome) => outcome.code === code,
      )
    ) { this.restore(model, this.draft?.acceptedOutcomeCodes.includes(code) ?? false); return; }
    this.draft.acceptedOutcomeCodes = this.draft.acceptedOutcomeCodes.filter(
      (value) => value !== code,
    );
    if (checked) this.draft.acceptedOutcomeCodes.push(code);
    this.changed();
  }
  referenceIdentity = childReferenceIdentity;
  beginOutcome(origin?: ChildFormOrigin) {
    if (this.workspaceMode && this.dirty && this.nativeCurrent(origin)) {
      this.error = 'Apply or cancel the current workflow result input before opening its settings again.'; this.focusPendingChanges(); return;
    }
    if (
      !this.nativeCurrent(origin) ||
      this.disabled ||
      this.interactionBlocked ||
      (!this.node?.terminalOutcome && this.editingDisabled) ||
      !this.terminal ||
      !this.outcomeEditable ||
      !this.applyPendingChanges()
    )
      return;
    this.fingerprint = childWorkFingerprint(this.document!, this.nodeId);
    this.originalOutcomeCode = this.node?.terminalOutcome?.code ?? null;
    this.outcomeDraft = structuredClone(
      this.node?.terminalOutcome ?? { version: 1, code: '', label: '' },
    );
    this.formGeneration++;
    if (!this.node?.terminalOutcome) this.changed();
    this.focusPendingChanges();
  }
  outcomeLabelChanged(label: string, origin?: ChildFormOrigin, model?: NgModel) {
    if (!this.nativeCurrent(origin) || this.editingDisabled || !this.outcomeDraft) { this.restore(model, this.outcomeDraft?.label); return; }
    this.outcomeDraft.label = label;
    this.outcomeDraft.code =
      this.originalOutcomeCode ?? terminalOutcomeCode(label);
    this.changed();
  }
  changed(origin?: ChildFormOrigin) {
    if (!this.nativeCurrent(origin) || this.editingDisabled) return;
    this.dirty = true;
    this.error = '';
    this.pendingChangesChange.emit(true);
  }
  hasPendingChanges() {
    return this.dirty;
  }
  canSettleExistingChanges(): boolean {
    return this.workspaceMode && this.dirty && !this.pickerOpen && this.bufferCurrent(this.currentDocument())
      && (this.existingRelatedInput() || this.existingOutcomeInput(this.currentDocument()));
  }
  private existingRelatedInput(): boolean {
    return !!this.draft && !!this.originalId && !this.outcomeDraft && this.rules.filter(rule => rule?.id === this.originalId).length === 1;
  }
  private existingOutcomeInput(document: WorkflowDefinitionDocument | null): boolean {
    if (!this.outcomeDraft || this.draft || !this.originalOutcomeCode || !document || !Array.isArray(document.nodes)) return false;
    const targets = document.nodes.filter(node => node?.id === this.nodeId);
    const saved = targets.length === 1 && targets[0].typeCode === 'terminal' ? targets[0].terminalOutcome : null;
    return !!saved && saved.version === 1 && childCode(saved.code) && typeof saved.label === 'string'
      && Object.keys(saved).every(key => ['version', 'code', 'label'].includes(key))
      && saved.code === this.originalOutcomeCode && this.outcomeDraft.code === saved.code
      && document.nodes.filter(node => node?.typeCode === 'terminal' && node.terminalOutcome?.code === saved.code).length === 1;
  }
  settleExistingChanges(document: WorkflowDefinitionDocument): boolean {
    return !this.dirty || this.canSettleExistingChanges() && this.applyPendingChanges(document);
  }
  /** Existing supported settings are input; new relationships and results stay deliberate candidates. */
  pendingEditorScopes(latestDocument: () => WorkflowDefinitionDocument | null = () => this.currentDocument()): readonly WorkflowEditorPendingScope[] {
    return (['related', 'result'] as const).map(kind => {
      const existingInput = this.workspaceMode && !this.pickerOpen && (kind === 'related' ? this.existingRelatedInput() : this.existingOutcomeInput(latestDocument()));
      return ({
      id: kind === 'related' ? 'related-work-apply' : 'workflow-result-apply',
      label: kind === 'related' ? existingInput ? 'related-work settings' : 'related work awaiting Apply' : existingInput ? 'workflow result settings' : 'workflow result awaiting Apply',
      kind: existingInput ? 'input' as const : 'modal-candidate' as const,
      ...(existingInput ? { apply: () => { const document = latestDocument(); return !!document && this.settleExistingChanges(document); } } : {}),
      state: (): WorkflowEditorBufferState => {
        if (!this.dirty || !(kind === 'related' ? this.draft : this.outcomeDraft)) return 'clean';
        const document = latestDocument();
        if (!document || !this.nodeId || !this.bufferCurrent(document) || childWorkFingerprint(document, this.nodeId) !== this.fingerprint) return 'stale';
        const nodes = document.nodes.filter(node => node.id === this.nodeId);
        if (nodes.length !== 1 || nodes[0].typeCode !== (kind === 'related' ? 'human_step' : 'terminal')) return 'stale';
        if (this.editingDisabled) return 'invalid';
        try { this.candidateDocument(document, kind); return 'valid'; } catch { return 'invalid'; }
      },
      fingerprint: () => {
        const document = latestDocument();
        return JSON.stringify([this.dirty, kind, kind === 'related' ? this.draft : this.outcomeDraft,
          this.originalId, this.originalOutcomeCode, this.fingerprint, this.rules, this.nodeId, this.resetVersion,
          this.disabled, this.canAuthor, this.interactionBlocked, this.loading, this.catalogReady, this.childWorkUnavailableReason, this.choices, this.ownerSnapshot, this.ownerKey(), this.namespaceSnapshot, this.pickerOpen,
          document && [childWorkFingerprint(document, this.nodeId), document.payloadSchemaVersion]]);
      },
      discard: () => {
        if (kind === 'related' && this.pickerOpen) { this.pickerOpen = false; this.pickerSelected = ''; this.pickerGeneration++; }
        if (!(kind === 'related' ? this.draft : this.outcomeDraft) || !this.dirty) return;
        const document = latestDocument();
        if (document) this.document = document;
        if (kind === 'related') { this.draft = null; this.originalId = null; }
        else { this.outcomeDraft = null; this.originalOutcomeCode = null; }
        this.dirty = !!this.draft || !!this.outcomeDraft;
        if (!this.dirty) {
          this.context = this.documentContext(this.document);
          this.fingerprint = '';
          this.error = '';
          this.rules = this.savedRules(); this.ownerSnapshot = this.ownerKey(); this.namespaceSnapshot = this.namespace(); this.formGeneration++;
        }
        this.pendingChangesChange.emit(this.dirty);
      },
      focus: () => this.focusPendingChanges(),
    }); });
  }
  private candidateDocument(document: WorkflowDefinitionDocument, kind: 'related' | 'result'): WorkflowDefinitionDocument {
    if (!this.nodeId) throw new Error('Select one workflow step.');
    if (this.directMutation) return this.directMutation.kind === 'result' ? applyTerminalOutcome(document, this.nodeId, null)
      : applyChildLinks(document, this.nodeId, this.rules.filter(rule => rule?.id !== this.directMutation!.id), this.rules, this.choices);
    if (kind === 'result') {
      if (!this.outcomeDraft) throw new Error('Enter a workflow result.');
      return applyTerminalOutcome(document, this.nodeId, { ...this.outcomeDraft, label: this.outcomeDraft.label.trim() });
    }
    if (!this.draft) throw new Error('Enter a related-work rule.');
    const draft = { ...this.draft, label: this.draft.label.trim() } as WorkflowStepChildLink;
    const rules = this.originalId ? this.rules.map(rule => rule.id === this.originalId ? draft : rule) : [...this.rules, draft];
    return applyChildLinks(document, this.nodeId, rules, this.rules, this.choices);
  }
  isCurrentOwnedDocumentChange(event: WorkflowChildWorkOwnedDocumentChange, current: WorkflowDefinitionDocument): boolean {
    if (!this.workspaceMode || this.emitting !== event || !this.dirty || !this.bufferCurrent(current) || this.disabled || !this.canAuthor
      || event.nodeId !== this.nodeId || event.owner !== this.ownerKey() || event.source !== JSON.stringify(current)
      || JSON.stringify(event.draft) !== JSON.stringify(this.draftSnapshot)) return false;
    try { return JSON.stringify(this.candidateDocument(current, event.draft.kind === 'result' ? 'result' : 'related')) === JSON.stringify(event.document); }
    catch { return false; }
  }
  private acceptCandidate(current: WorkflowDefinitionDocument, next: WorkflowDefinitionDocument): boolean {
    if (!this.workspaceMode) { this.document = next; this.resetPendingChanges(); this.documentChange.emit(next); return true; }
    const owner = this.ownerKey(), namespace = this.namespace(), draft = JSON.stringify(this.draftSnapshot), expected = JSON.stringify(next);
    const event: WorkflowChildWorkOwnedDocumentChange = { document: next, owner, source: JSON.stringify(current), view: this.activeView?.() ?? null,
      nodeId: this.nodeId!, draft: this.draftSnapshot };
    this.emitting = event;
    try { this.ownedDocumentChange.emit(event); } finally { this.emitting = null; }
    const receipt = this.currentDocument();
    if (!receipt || receipt === current || owner !== this.ownerKey() || namespace !== this.namespace()
      || draft !== JSON.stringify(this.draftSnapshot) || expected !== JSON.stringify(receipt)) {
      this.error = 'Related-work input was not applied to the current draft. Your input is retained.'; return false;
    }
    this.document = receipt; this.resetPendingChanges(); return true;
  }
  applyPendingChanges(document = this.currentDocument()) {
    if (this.emitting) return false;
    if (!this.dirty) return true;
    if (!document || !this.nodeId || this.editingDisabled) return false;
    if (!this.bufferCurrent(document) || this.workspaceMode && JSON.stringify(document) !== JSON.stringify(this.currentDocument()) || childWorkFingerprint(document, this.nodeId) !== this.fingerprint) {
      this.error =
        'This step, result or child reference changed while the editor was open. Your input is kept; discard and reopen the current settings.';
      return false;
    }
    try {
      const next = this.candidateDocument(document, this.terminal ? 'result' : 'related');
      return this.acceptCandidate(document, next);
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : 'The related-work settings could not be applied.';
      return false;
    }
  }
  apply(origin?: ChildFormOrigin) {
    if (!this.nativeCurrent(origin)) return;
    if (!this.applyPendingChanges()) this.focusPendingChanges();
  }
  remove(rule: WorkflowStepChildLink, origin?: ChildFormOrigin) {
    if (this.workspaceMode && this.dirty) { this.error = 'Apply or cancel the current input before removing related work.'; this.focusPendingChanges(); return; }
    if (
      !this.nativeCurrent(origin) || !this.rules.includes(rule) || this.editingDisabled ||
      this.ambiguous ||
      !this.applyPendingChanges() ||
      !this.document ||
      !this.nodeId
    )
      return;
    try {
      const next = applyChildLinks(
        this.document,
        this.nodeId,
        this.rules.filter((item) => item.id !== rule.id),
        this.rules,
        this.choices,
      );
      if (this.workspaceMode) {
        this.fingerprint = childWorkFingerprint(this.currentDocument()!, this.nodeId); this.directMutation = { kind: 'related', id: rule.id }; this.dirty = true;
        try { if (!this.acceptCandidate(this.currentDocument()!, next)) this.dirty = false; } finally { this.directMutation = null; }
      } else { this.document = next; this.resetPendingChanges(); this.documentChange.emit(next); }
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : 'The rule could not be removed.';
    }
  }
  removeOutcome(origin?: ChildFormOrigin) {
    if (this.workspaceMode && this.dirty) { this.error = 'Apply or cancel the current result input before removing its mapping.'; this.focusPendingChanges(); return; }
    if (
      !this.nativeCurrent(origin) || this.editingDisabled ||
      !this.applyPendingChanges() ||
      !this.document ||
      !this.nodeId
    )
      return;
    const next = applyTerminalOutcome(this.document, this.nodeId, null);
    if (this.workspaceMode) {
      this.fingerprint = childWorkFingerprint(this.currentDocument()!, this.nodeId); this.directMutation = { kind: 'result' }; this.dirty = true;
      try { if (!this.acceptCandidate(this.currentDocument()!, next)) this.dirty = false; } finally { this.directMutation = null; }
    } else { this.document = next; this.resetPendingChanges(); this.documentChange.emit(next); }
  }
  resetPendingChanges() {
    this.rules = this.savedRules();
    this.draft = null;
    this.outcomeDraft = null;
    this.originalId = null;
    this.originalOutcomeCode = null;
    this.fingerprint = '';
    this.dirty = false;
    this.ownerSnapshot = this.ownerKey(); this.namespaceSnapshot = this.namespace(this.document); this.formGeneration++; this.focusRequest++;
    this.error = '';
    this.pendingChangesChange.emit(false);
  }
  discardPendingChanges() {
    this.document = this.currentDocument(); this.context = this.documentContext(this.document);
    this.resetPendingChanges();
  }
  discardVisibleChanges(origin?: ChildFormOrigin): void { if (this.nativeCurrent(origin)) this.discardPendingChanges(); }
  capturePendingChildWorkInputs(): readonly { name: string; label: string; value: string }[] {
    return !this.dirty && !this.pickerOpen ? [] : [{ name: `child-work-input:${encodeURIComponent(this.nodeId ?? 'unselected')}`,
      label: `Unapplied related work or workflow result for ${this.nodeId ?? 'unselected'}`,
      value: JSON.stringify({ format: 'phms.workflow-child-work-input', version: 1, nodeId: this.nodeId,
        draft: this.draftSnapshot, context: this.context, originalOwner: this.ownerSnapshot, originalNamespace: this.namespaceSnapshot,
        picker: this.pickerOpen ? { purpose: this.pickerPurpose, selected: this.pickerSelected, source: this.pickerSource, baseline: this.pickerBaseline } : null }) }];
  }
  focusPendingChanges() {
    const request = ++this.focusRequest, owner = this.ownerKey(), source = JSON.stringify(this.currentDocument()), view = this.activeView?.() ?? null,
      generation = this.formGeneration, before = this.element.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.element.nativeElement, active = host.ownerDocument.activeElement;
      if (this.destroyed || request !== this.focusRequest || owner !== this.ownerKey() || source !== JSON.stringify(this.currentDocument()) || view !== (this.activeView?.() ?? null)
        || generation !== this.formGeneration || this.workspaceMode && (!this.presentationCurrent() || this.pickerOpen) || this.ownerViews.focusSuspended() || host.closest('[hidden],[inert]')) return;
      if (active !== before && active !== host.ownerDocument.body && active?.isConnected && !active.closest('[hidden],[inert]')) return;
      const target = host.querySelector<HTMLElement>('[role="alert"][tabindex]') ?? host.querySelector<HTMLElement>('.child-form input:not(:disabled),.child-form select:not(:disabled),h4[tabindex]');
      if (target?.isConnected) { target.focus({ preventScroll: this.workspaceMode }); if (this.workspaceMode && host.ownerDocument.activeElement === target
        && owner === this.ownerKey() && source === JSON.stringify(this.currentDocument()) && view === (this.activeView?.() ?? null) && this.presentationCurrent()) target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' }); }
    });
  }
}
